<template>
   <b-container>
      <h1 class="mb-3">Fillable PDF Forms for Delivery to Clients</h1>

      <div v-if="taxForms.length > 0" class="missing-pdf-section">
         <h2>
            <span><b-icon-exclamation-triangle-fill variant="warning" class="warning-icon" /></span
            >Calculations with no PDF
         </h2>
         <p>
            The following forms are needed. Create a fillable PDF form with fillable fields using
            the Line IDs exactly as provided below and upload the form below.
         </p>

         <CollapseCard
            v-for="taxForm in taxForms"
            :ident="`tax-form-${taxForm.id}`"
            :key="taxForm.id"
            :title="taxForm.name"
            :subtitle="`${taxForm.lines.length} lines`"
            class="tax-form-card"
         >
            <b-table :fields="taxReturnFormFields" :items="taxForm.lines" class="mb-0" responsive>
            </b-table>
         </CollapseCard>
      </div>

      <div class="fillable-pdfs-section">
         <h2>Fillable PDFs</h2>
         <b-table
            v-if="fillablePdfItems.length > 0"
            :fields="fillablePdfFields"
            :items="fillablePdfItems"
            class="mb-0"
            responsive
         >
            <template #cell(actions)="data">
               <div class="d-flex align-items-center justify-content-end cell-w-buttons">
                  <b-button
                     :id="`btn-delete-${data.item.id}`"
                     size="sm"
                     variant="danger"
                     @click="onDeleteFillablePdf(data.item)"
                  >
                     Delete
                  </b-button>
               </div>
            </template>
         </b-table>
         <p v-else>No fillable PDFs have been uploaded yet.</p>
      </div>

      <div class="upload-fillable-pdf-section">
         <h2>Upload a new PDF</h2>
         <div class="fillable-pdf-form-wrapper">
            <b-form-group class="pdf-name-input" label="Name">
               <b-form-input
                  id="input-fillable-pdf-name"
                  :value="fillablePdf.name"
                  v-model="fillablePdf.name"
                  :state="!$v.fillablePdf.name.$dirty ? null : !$v.fillablePdf.name.$invalid"
               ></b-form-input>
            </b-form-group>

            <b-form-group class="pdf-state-input" label="State">
               <b-form-select
                  id="input-fillable-pdf-state"
                  v-model="fillablePdf.state"
                  :options="statesOptions"
               ></b-form-select>
            </b-form-group>

            <b-form-group
               label="Choose PDF File"
               label-for="fillable-pdf-file"
               class="pdf-file-input"
            >
               <b-form-file id="fillable-pdf-file" v-model="fillablePdf.file"> </b-form-file>
            </b-form-group>

            <b-button
               class="upload-btn"
               variant="primary"
               size="sm"
               @click="uploadFillableForm()"
               :disabled="$v.fillablePdf.$invalid"
               >Upload</b-button
            >
         </div>
      </div>
   </b-container>
</template>

<script>
import {minLength, required} from 'vuelidate/lib/validators';
import {mapGetters} from 'vuex';

export default {
   name: 'AdminDeliverableForms',

   data() {
      return {
         fillablePdf: {
            name: '',
            state: 'US',
            file: null,
         },
      };
   },

   computed: {
      ...mapGetters({
         usStates: 'usStates',
         fillablePdfData: 'uploads/fillablePDFs',
         missingTaxForms: 'taxReturn/missingTaxReturns',
      }),
      statesOptions() {
         const states = Object.entries(this.usStates)
            .sort((a, b) => {
               const [aKey, aName] = a;
               const [bKey, bName] = b;

               if (aKey === 'XX') {
                  return -1;
               } else if (bKey === 'XX') {
                  return 1;
               }

               if (aName < bName) {
                  return 1;
               } else if (aName < bName) {
                  return -1;
               }
               return 0;
            })
            .map((entry) => {
               const [key, value] = entry;
               return {text: value, value: key};
            });
         return [{text: 'Federal', value: 'US'}, ...states];
      },
      taxForms() {
         return this.missingTaxForms.map((taxForm, index) => {
            return {
               id: index,
               name: taxForm.formName,
               lines: taxForm.lines,
            };
         });
      },
      fillablePdfItems() {
         return this.fillablePdfData.map((pdf) => {
            const state = this.statesOptions.find((state) => state.value === pdf.location);
            return {
               id: pdf.id,
               name: pdf.objName,
               state: state.text,
            };
         });
      },
      fillablePdfFields() {
         return [
            {key: 'name', label: 'Form Name', sortable: true},
            {key: 'state', label: 'State', sortable: true},
            {key: 'actions', sortable: false, label: ''},
         ];
      },
      taxReturnFormFields() {
         return [
            {key: 'id', label: 'Line ID', class: 'missing-tax-forms-id-column'},
            {key: 'description', label: 'Line Description'},
         ];
      },
   },

   methods: {
      async uploadFillableForm() {
         await this.$store.dispatch('uploads/saveFillableFile', this.fillablePdf);
         this.resetFillablePdfForm();
      },

      resetFillablePdfForm() {
         this.fillablePdf = {
            name: '',
            state: 'US',
            file: null,
         };
      },
      async onDeleteFillablePdf(item) {
         await this.$store.dispatch('uploads/deleteFillableFile', {fileId: item.id});
      },
   },

   async created() {
      let requests = [
         this.$store.dispatch('uploads/getFillableFiles'),
         this.$store.dispatch('taxReturn/loadMissingTaxReturns'),
      ];
      await this.loadPage(requests);
   },

   validations: {
      fillablePdf: {
         name: {
            required,
            minLength: minLength(3),
         },
         state: {
            required,
         },
         file: {
            required,
         },
      },
   },
};
</script>

<style scoped lang="scss">
.missing-pdf-section {
   display: flex;
   flex-direction: column;
   gap: 0.5rem;
   padding: 1rem 0;
}
.warning-icon {
   font-size: 1.5rem;
   padding-right: 0.25rem;
}
.fillable-pdfs-section {
   display: flex;
   flex-direction: column;
   gap: 1rem;
   padding: 1rem 0;
}
.upload-fillable-pdf-section {
   display: flex;
   flex-direction: column;
   gap: 1rem;
   padding: 1rem 0;
}
.tax-form-card {
   margin: 0;
}
.fillable-pdf-form-wrapper {
   display: flex;
   flex-direction: column;
   gap: 1rem;
}
.upload-btn {
   width: 6rem;
}
.pdf-name-input {
   width: 22rem;
}
.pdf-state-input {
   width: 22rem;
}
.pdf-file-input {
   width: 22rem;
}
::v-deep .missing-tax-forms-id-column {
   min-width: 7rem;
}
</style>
